import { api, ApiFehler, type Aufnahme } from './api'

/**
 * Offline-Warteschlange: Jede Aufnahme landet zuerst in IndexedDB und wird erst nach erfolgreichem Upload entfernt.
 * So geht nichts verloren, wenn das Netz weg ist oder die App mitten im Upload geschlossen wird.
 */

export interface Wartend extends Aufnahme {
  /** Letzte Fehlermeldung, wenn der Server die Aufnahme abgelehnt hat (kein Netzproblem) */
  fehler?: string
}

const DB_NAME = 'meineperlen'
const STORE = 'aufnahmen'

function oeffnen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'clientId' })
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function mitStore<T>(modus: IDBTransactionMode, aktion: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await oeffnen()
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, modus)
      const req = aktion(tx.objectStore(STORE))
      tx.oncomplete = () => resolve(req.result)
      tx.onerror = () => reject(tx.error)
      tx.onabort = () => reject(tx.error)
    })
  } finally {
    db.close()
  }
}

export const warteschlange = {
  ablegen: (a: Wartend) => mitStore('readwrite', (s) => s.put(a)).then(() => undefined),
  alle: () => mitStore<Wartend[]>('readonly', (s) => s.getAll() as IDBRequest<Wartend[]>),
  entfernen: (clientId: string) => mitStore('readwrite', (s) => s.delete(clientId)).then(() => undefined),
}

/** Server lehnt die Aufnahme inhaltlich ab: erneutes Senden hilft nicht. */
export function endgueltigAbgelehnt(e: unknown): boolean {
  return e instanceof ApiFehler && e.status >= 400 && e.status < 500 && ![401, 403, 408, 429].includes(e.status)
}

let laeuft: Promise<number> | null = null

/** Laedt alle wartenden Aufnahmen hoch. Liefert die Zahl der erfolgreich hochgeladenen. Laeuft nie doppelt. */
export function abarbeiten(): Promise<number> {
  laeuft ??= (async () => {
    let erfolgreich = 0
    for (const a of await warteschlange.alle()) {
      if (a.fehler) continue
      try {
        await api.aufnahmeHochladen(a)
        await warteschlange.entfernen(a.clientId)
        erfolgreich++
      } catch (e) {
        if (endgueltigAbgelehnt(e)) {
          await warteschlange.ablegen({ ...a, fehler: (e as Error).message })
        } else {
          break // offline oder abgemeldet: spaeter erneut
        }
      }
    }
    return erfolgreich
  })().finally(() => {
    laeuft = null
  })
  return laeuft
}
